import { useMemo } from "react";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { IPlannyAccessLogItem } from "../../../../common/models/plannyAccessLogItemModel";
import { StudioAuditTimelineAction } from "../../../../common/enums";
import {
  PLANNY_ACCESS_LOG_MAX_ENTRIES,
  formatAccessDateTime,
  getAccessActionLabel,
  getAccessActorLabel,
  getRelativeAccessLabel,
  groupAccessLogByMonth,
} from "../../../../common/utilities/plannyAccessUtility";

/**
 * The owner's "Достъп от Planny" tab: every time a global admin opened one of their studios,
 * changed its settings, or opened someone's profile in it.
 */
export const usePlannyAccessSection = () => {
  const { data, isLoading, isError } = useApiQuery<IPlannyAccessLogItem[]>({
    queryKey: [endpointsMetadata.studioAuditLog.getMyStudioAccessLog],
    endpoint: endpointsMetadata.studioAuditLog.getMyStudioAccessLog,
    globalIsLoading: false,
    retry: false,
  });

  return useMemo(() => {
    const items = data ?? [];
    const now = new Date();

    const groups = groupAccessLogByMonth(items).map((group) => ({
      key: group.key,
      label: group.label,
      entries: group.items.map((item) => ({
        id: item.id,
        isChange: item.action === StudioAuditTimelineAction.GlobalAdminEditedSettings,
        isExit: item.action === StudioAuditTimelineAction.GlobalAdminLeft,
        actionLabel: getAccessActionLabel(item.action, item.section, item.profileName),
        reasonLabel: item.reason ? `„${item.reason}“` : null,
        changedFieldsLabel: item.changedFields?.length ? item.changedFields.join(", ") : null,
        actorLabel: getAccessActorLabel(item.actorDisplayName),
        studioName: item.studioName,
        relative: getRelativeAccessLabel(item.timestamp, now),
        ...formatAccessDateTime(item.timestamp),
      })),
    }));

    const changeCount = items.filter(
      (item) => item.action === StudioAuditTimelineAction.GlobalAdminEditedSettings,
    ).length;
    // Opening someone's profile in the studio is an opening too.
    const openCount = items.filter(
      (item) =>
        item.action === StudioAuditTimelineAction.GlobalAdminAccessed ||
        item.action === StudioAuditTimelineAction.GlobalAdminOpenedProfile,
    ).length;
    // The API returns only the newest entries; past the cap the counts are a lower bound.
    const countLabel = (count: number) =>
      items.length >= PLANNY_ACCESS_LOG_MAX_ENTRIES ? `${count}+` : String(count);

    return {
      isLoading,
      // A failed load must never read as "nobody opened your studios".
      hasLoadFailed: isError,
      isEmpty: !isLoading && !isError && items.length === 0,
      openCountLabel: countLabel(openCount),
      openCountCaption: openCount === 1 ? "отваряне" : "отваряния",
      totalEntries: items.length,
      changeCountLabel: countLabel(changeCount),
      changeCountCaption: changeCount === 1 ? "промяна" : "промени",
      lastActivityLabel: items[0] ? getRelativeAccessLabel(items[0].timestamp, now) : "—",
      groups,
    };
  }, [data, isLoading, isError]);
};
